import { useCallback } from 'react';
import type React from 'react';

import toast from 'components/partials/toast/toast';

import type { RegimeGroup } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface UpdateGroupMutation {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface UseRegimesSidebarGroupActionsParams {
  userId: string;
  modelId: string | number;
  localGroups: RegimeGroup[];
  setLocalGroups: React.Dispatch<React.SetStateAction<RegimeGroup[]>>;
  hasPendingGroupUpdate: boolean;
  pendingGroupUpdateRef: React.MutableRefObject<boolean>;
  addPendingGroup: (groupId: string | number) => void;
  removePendingGroup: (groupId: string | number) => void;
  updateGroupMut: UpdateGroupMutation;
}

interface UseRegimesSidebarGroupActionsResult {
  handleGroupToggle: (group: RegimeGroup, checked: boolean) => void;
}

export const useRegimesSidebarGroupActions = ({
  userId,
  modelId,
  localGroups,
  setLocalGroups,
  hasPendingGroupUpdate,
  pendingGroupUpdateRef,
  addPendingGroup,
  removePendingGroup,
  updateGroupMut,
}: UseRegimesSidebarGroupActionsParams): UseRegimesSidebarGroupActionsResult => {
  const handleGroupToggle = useCallback(
    (group: RegimeGroup, checked: boolean) => {
      if (pendingGroupUpdateRef.current) return;
      if (hasPendingGroupUpdate) return;

      const previousGroups = localGroups;

      pendingGroupUpdateRef.current = true;
      addPendingGroup(group.id);
      setLocalGroups((prev) =>
        prev.map((g) =>
          g.id === group.id
            ? { ...g, enabled: checked }
            : { ...g, enabled: checked ? false : g.enabled }
        )
      );

      updateGroupMut
        .mutateAsync({
          userId,
          modelId,
          groupId: group.id,
          data: {
            name: group.name,
            enabled: checked,
            sortOrder: group.sortOrder,
          },
        })
        .catch((error) => {
          setLocalGroups(previousGroups);
          toast.error({
            title: 'Error',
            message:
              error?.data?.detail ||
              'Failed to update group. Please try again.',
          });
        })
        .finally(() => {
          pendingGroupUpdateRef.current = false;
          removePendingGroup(group.id);
        });
    },
    [
      addPendingGroup,
      hasPendingGroupUpdate,
      localGroups,
      pendingGroupUpdateRef,
      removePendingGroup,
      setLocalGroups,
      updateGroupMut,
      userId,
      modelId,
    ]
  );

  return {
    handleGroupToggle,
  };
};
